/**
 * Chat Page - Chatbot Preview with Session History
 */

import { isAdminRole } from '../utils/role';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Badge, Button, SearchInput, Tabs, useToast } from '../components/ui';
import { FlowSteps } from '../components/workflow';
import ThemeIcon from '../assets/icons/common/theme.svg?react';
import { workflowApi, Workflow, ChatMessage as ApiChatMessage, ChatSource, apiClient } from '../api';
import { authApi } from '../api';
import { chatSessionApi, ChatSessionItem, ChatTheme } from '../api/chat-sessions';
import { nanoid } from 'nanoid';
import { ThemeEditorModal } from '../components/chat/ThemeEditorModal';
import { calcCostFront } from '../utils/model-pricing';
import { ChatRenderer, Message, TracePanel, SourceList } from '../components/chat/ChatRenderer';
import '../styles/chat.css';
import '../styles/flowCard.css';

// TracePanel, SourceList re-export (하위 호환)
export { TracePanel, SourceList };

// ── 챗봇 선택 그리드 화면 ─────────────────────────────────────────────────────
type ChatTab = 'all' | 'shared' | 'mine' | 'userCreated';

function ChatSelectPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [chatbots, setChatbots] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<ChatTab>('all');

  const [themeModalBot, setThemeModalBot] = useState<Workflow | null>(null);
  const [savingTheme, setSavingTheme] = useState(false);
  const [cardThemes, setCardThemes] = useState<Record<string, ChatTheme>>({});

  const currentUser = authApi.getUser();
  const isAdmin = isAdminRole(currentUser?.role);

  useEffect(() => {
    const currentUser = authApi.getUser();
    Promise.all([
      workflowApi.list({}),
      workflowApi.list({ deployed: true }),
    ]).then(([ownData, sharedData]) => {
      const triggerFilter = (w: Workflow) => w.nodes.some(n => n.type === 'trigger');
      const shared = sharedData.filter(triggerFilter);
      const mine = ownData.filter(w =>
        w.owner === currentUser?.email && w.status === 'deployed' && triggerFilter(w)
      );
      const allMap = new Map<string, Workflow>();
      [...shared, ...mine].forEach(w => allMap.set(w.id, w));
      const bots = [...allMap.values()];
      setChatbots(bots);
      bots.forEach(bot => {
        chatSessionApi.getTheme(bot.id).then(theme => {
          if (theme) setCardThemes(prev => ({ ...prev, [bot.id]: theme }));
        }).catch(() => {});
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const isVisibleToUser = (w: Workflow) => w.isSharedAsChatbot || w.owner === currentUser?.email;
  const filtered = chatbots.filter(w => {
    const tabMatch = isAdmin
      ? activeTab === 'shared' ? w.isSharedAsChatbot
        : activeTab === 'userCreated' ? w.owner === currentUser?.email : true
      : activeTab === 'all' ? isVisibleToUser(w)
        : activeTab === 'shared' ? w.isSharedAsChatbot
        : w.owner === currentUser?.email;
    if (!tabMatch) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return w.name.toLowerCase().includes(q) || (w.description || '').toLowerCase().includes(q);
  }).sort((a, b) => {
    const aShared = a.isSharedAsChatbot ? 0 : a.owner === currentUser?.email ? 1 : 2;
    const bShared = b.isSharedAsChatbot ? 0 : b.owner === currentUser?.email ? 1 : 2;
    if (aShared !== bShared) return aShared - bShared;
    return new Date(b.deployedAt || b.createdAt || 0).getTime() - new Date(a.deployedAt || a.createdAt || 0).getTime();
  });

  const allCount = chatbots.filter(isVisibleToUser).length;
  const sharedCount = chatbots.filter(w => w.isSharedAsChatbot).length;
  const mineCount = chatbots.filter(w => w.owner === currentUser?.email).length;

  const openThemeModal = async (bot: Workflow) => {
    const existing = await chatSessionApi.getTheme(bot.id).catch(() => null);
    if (existing) setCardThemes(prev => ({ ...prev, [bot.id]: existing }));
    setThemeModalBot(bot);
  };

  return (
    <>
      <Topbar title="챗봇" description="배포된 워크플로우를 테스트합니다" />
      <div className="chat-select-page">
        <div className="tabs-row">
          <Tabs<ChatTab>
            items={isAdmin
              ? [
                  { key: 'all',         label: <>전체<span className="tab-count">{chatbots.length}</span></> },
                  { key: 'shared',      label: <>관리자 등록<span className="tab-count">{sharedCount}</span></> },
                  { key: 'userCreated', label: <>사용자 등록<span className="tab-count">{mineCount}</span></> },
                ]
              : [
                  { key: 'all',    label: <>전체<span className="tab-count">{allCount}</span></> },
                  { key: 'shared', label: <>관리자 등록<span className="tab-count">{sharedCount}</span></> },
                  { key: 'mine',   label: <>내 챗봇<span className="tab-count">{mineCount}</span></> },
                ]}
            active={activeTab}
            onChange={setActiveTab}
          />
          <div className="tabs-row-aside">
            <SearchInput
              placeholder="챗봇 검색"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <p>
              {activeTab === 'all' ? '사용할 수 있는 챗봇이 없습니다.'
                : activeTab === 'shared' ? '관리자가 공유한 챗봇이 없습니다.'
                : activeTab === 'userCreated' ? '사용자가 등록한 챗봇이 없습니다.' : '내 챗봇이 없습니다.'}
            </p>
          </div>
        ) : (
          <div className="card-grid flow-card-grid">
            {filtered.map(bot => {
              const isShared = bot.isSharedAsChatbot;
              const date = bot.deployedAt || bot.createdAt;
              const dateText = date ? new Date(date).toLocaleDateString() : '-';
              return (
                <div key={bot.id} className="item-card flow-card">
                  <div className="flow-card-head">
                    <span className={`item-card-icon item-card-icon--letter ${isShared ? 'item-card-icon--admin' : 'item-card-icon--user'}`}>
                      {bot.name.trim().charAt(0).toUpperCase()}
                    </span>
                    <div className="flow-card-info">
                      <div className="flow-card-title"><span title={bot.name} style={{ fontWeight: 600 }}>{bot.name}</span></div>
                      <p>{bot.description || '설명 없음'}</p>
                    </div>
                  </div>
                  <FlowSteps workflow={bot} />
                  <div className="flow-card-foot">
                    <span className="flow-card-meta">
                      {bot.ownerName && <span className="flow-card-owner">{bot.ownerName}</span>}
                      <span>{dateText}</span>
                    </span>
                    <div className="flow-card-actions">
                      <Button size="sm" color="gray" appearance="outline" onClick={() => openThemeModal(bot)}>테마 설정</Button>
                      <Button size="sm" color="primary" onClick={() => navigate(`/chat/${bot.id}`)}>대화하기</Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ThemeEditorModal
        isOpen={!!themeModalBot}
        onClose={() => setThemeModalBot(null)}
        initialTheme={themeModalBot ? (cardThemes[themeModalBot.id] || {}) : {}}
        botName={themeModalBot?.name}
        saving={savingTheme}
        showQuickPrompts={isAdmin || (!!themeModalBot && themeModalBot.owner === currentUser?.email)}
        onSave={async (theme) => {
          if (!themeModalBot) return;
          setSavingTheme(true);
          try {
            await chatSessionApi.saveTheme(themeModalBot.id, theme);
            setCardThemes(prev => ({ ...prev, [themeModalBot.id]: theme }));
            setThemeModalBot(null);
            showToast('success', '테마 설정이 저장되었습니다.');
          } catch { showToast('error', '테마 저장에 실패했습니다.'); }
          finally { setSavingTheme(false); }
        }}
      />
    </>
  );
}

// ── 챗봇 페이지 ───────────────────────────────────────────────────────────────
export function ChatPage() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const currentUser = authApi.getUser();
  const isAdmin = isAdminRole(currentUser?.role);

  // ── 워크플로우 / 메시지 / 스트리밍 상태 ──
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingStatus, setStreamingStatus] = useState('');
  const [streamingTrace, setStreamingTrace] = useState<Array<Record<string, unknown>>>([]);
  const streamingTraceRef = useRef<Array<Record<string, unknown>>>([]); // stale closure 방지
  const [streamingSources, setStreamingSources] = useState<ChatSource[]>([]);
  const [streamingChartData, setStreamingChartData] = useState<Record<string, unknown>[] | null>(null);

  // ── 세션 / 테마 ──
  const [sessionId] = useState<string>(() => nanoid(10));
  const [sessions, setSessions] = useState<ChatSessionItem[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [chatIdCounter, setChatIdCounter] = useState(1);
  const [personalTheme, setPersonalTheme] = useState<ChatTheme | null>(null);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);

  // ── HTML 미리보기 ──
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewWidth, setPreviewWidth] = useState(420);
  const previewWidthRef = useRef(420);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const layoutRef = useRef<HTMLDivElement>(null);

  // ── 선택 모델 (ChatRenderer에서 콜백으로 동기화) ──
  const selectedModelRef = useRef<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialMessageRef = useRef<string | null>(
    (location.state as { initialMessage?: string } | null)?.initialMessage ?? null
  );

  const activeTheme = personalTheme || {};
  const isBotOwner = !!workflow?.owner && workflow.owner === currentUser?.email;
  const llmNode = workflow?.nodes?.find(n => n.type === 'llm');
  const llmModels = (llmNode?.data?.models as string[] | undefined) || (llmNode?.data?.model ? [llmNode.data.model as string] : []);
  const llmModel = llmModels[0] || '';
  const retrievalNode = workflow?.nodes?.find(n => n.type === 'knowledge-retrieval');
  const topK = retrievalNode ? ((retrievalNode.data?.topK as number) || 5) : undefined;
  const botName = activeTheme.botName || workflow?.name || '챗봇';
  const quickPrompts = (activeTheme.quickPrompts || []).filter((p: string) => p.trim());
  const activeSessionId = currentSessionId || sessionId;

  // ── 세션 목록 로드 ──
  const loadSessions = useCallback(async (wfId?: string) => {
    try {
      const list = await chatSessionApi.listSessions(wfId || workflowId);
      setSessions(list);
    } catch { /* 무시 */ }
  }, [workflowId]);

  useEffect(() => {
    if (workflowId) loadSessions(workflowId);
  }, [workflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (workflowId) loadWorkflow(workflowId);
  }, [workflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: streamingContent ? 'instant' : 'smooth' });
  }, [messages, typing, streamingContent, streamingTrace]);

  // ── 워크플로우 로드 ──
  const loadWorkflow = async (id: string) => {
    try {
      setLoading(true);
      setCurrentSessionId(null);
      setChatIdCounter(1);
      const data = await workflowApi.get(id);
      if (data.status !== 'deployed') {
        showToast('error', '이 워크플로우는 배포되지 않았습니다.');
        navigate('/chat');
        return;
      }
      setWorkflow(data);
      setMessages([]);
      const theme = await chatSessionApi.getTheme(id).catch(() => null);
      setPersonalTheme(theme || null);

      const sid = searchParams.get('sessionId');
      if (sid) {
        try {
          const items = await chatSessionApi.getSession(sid);
          const paired: Message[] = [];
          items.forEach(i => {
            if (i.userContent) paired.push({ id: `u-${i.chatId}`, chatId: i.chatId, messageId: i.messageId, role: 'user', content: i.userContent });
            if (i.assistantContent) {
              const inputToken = i.inputToken ?? 0;
              const outputToken = i.outputToken ?? 0;
              const usage = (inputToken || outputToken) ? {
                inputToken, outputToken, totalToken: i.totalToken ?? 0,
                ...calcCostFront(i.modelId || '', inputToken, outputToken),
              } : undefined;
              const elapsedMs = i.resultTime ? parseFloat(i.resultTime) * 1000 : undefined;
              paired.push({ id: `a-${i.chatId}`, chatId: i.chatId, messageId: i.messageId, role: 'assistant', content: i.assistantContent, usage, sources: i.sources, elapsedMs, traceContent: i.traceContent?.length ? i.traceContent : undefined });
            }
          });
          setMessages(paired);
          setCurrentSessionId(sid);
          setChatIdCounter((items.length || 0) + 1);
        } catch { /* 무시 */ }
      }
    } catch {
      showToast('error', '워크플로우를 불러오는데 실패했습니다.');
      navigate('/chat');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const message = initialMessageRef.current;
    if (!workflow || workflow.id !== workflowId || !message) return;
    initialMessageRef.current = null;
    navigate(location.pathname, { replace: true, state: null });
    handleSend(message);
  }, [workflow]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 메시지 전송 ──
  const handleSend = async (text: string) => {
    if (!text.trim() || !workflowId || typing) return;

    const msgId = nanoid(10);
    const startedAt = performance.now();

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      chatId: String(chatIdCounter),
      messageId: msgId,
      role: 'user',
      content: text,
    };

    setMessages(prev => [...prev, userMessage]);
    setTyping(true);
    setStreamingContent('');
    setStreamingStatus('');
    setStreamingTrace([]);
    streamingTraceRef.current = [];
    setStreamingSources([]);

    const effectiveModelId = selectedModelRef.current || llmModel;
    const startTime = new Date().toISOString();
    let activeSessionId2 = currentSessionId;
    const currentChatId = chatIdCounter;

    try {
      const saved = await chatSessionApi.saveUserMessage({
        sessionId: activeSessionId2 || sessionId,
        chatId: String(currentChatId),
        workflowId,
        userContent: text,
        startDateTime: startTime,
        modelId: effectiveModelId,
        themeMode: 'light',
        messageId: msgId,
      });
      if (!activeSessionId2) {
        activeSessionId2 = saved.sessionId;
        setCurrentSessionId(saved.sessionId);
      }
      if (currentChatId === 1) {
        const title = text.slice(0, 30) + (text.length > 30 ? '...' : '');
        await chatSessionApi.renameSession(activeSessionId2, title).catch(() => {});
        loadSessions(workflowId);
      }
    } catch { /* 무시 */ }

    try {
      const history: ApiChatMessage[] = messages.map(m => ({ role: m.role, content: m.content }));
      const SSE_BASE = import.meta.env.DEV ? 'http://localhost:3002' : '';
      const token = apiClient.defaults.headers.common['Authorization'] as string || `Bearer ${localStorage.getItem('agentbuilder_token')}`;
      const response = await fetch(`${SSE_BASE}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token },
        body: JSON.stringify({
          workflowId,
          sessionId,
          chatId: String(currentChatId),
          messageId: msgId,
          message: text,
          history,
          ...(selectedModelRef.current && selectedModelRef.current !== llmModel ? { modelOverride: selectedModelRef.current } : {}),
        }),
      });

      if (!response.ok) throw new Error('스트리밍 요청 실패');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let sources: ChatSource[] = [];
      let chartData: Record<string, unknown>[] | null = null;
      let usage: { inputToken: number; outputToken: number; totalToken: number; costUsd?: number; costKrw?: number } | null = null;

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          let streamError: Error | null = null;
          for (const line of chunk.split('\n')) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'text') {
                  fullContent += data.data;
                  setStreamingContent(fullContent);
                  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
                } else if (data.type === 'status') {
                  setStreamingStatus(data.data as string);
                  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
                } else if (data.type === 'trace') {
                  const newTrace = [...streamingTraceRef.current, data.data as Record<string, unknown>];
                  streamingTraceRef.current = newTrace;
                  setStreamingTrace(newTrace);
                  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
                } else if (data.type === 'sources') {
                  sources = data.data;
                  setStreamingSources(sources);
                } else if (data.type === 'chart') {
                  chartData = data.data as Record<string, unknown>[];
                  setStreamingChartData(chartData);
                } else if (data.type === 'usage') {
                  usage = data.data as { inputToken: number; outputToken: number; totalToken: number; costUsd?: number; costKrw?: number };
                } else if (data.type === 'error') {
                  streamError = new Error(typeof data.data === 'string' ? data.data : '오류가 발생했습니다.');
                }
              } catch { /* JSON 파싱 오류 무시 */ }
            }
          }
          if (streamError) throw streamError;
        }
      }

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        chatId: String(currentChatId),
        messageId: msgId,
        role: 'assistant',
        content: fullContent,
        sources,
        chartData: chartData ?? undefined,
        usage: usage ?? undefined,
        elapsedMs: performance.now() - startedAt,
        traceContent: streamingTraceRef.current.length > 0 ? [...streamingTraceRef.current] : undefined,
      };

      setMessages(prev => [...prev, assistantMessage]);
      setStreamingContent('');
      setStreamingStatus('');
      setStreamingTrace([]);
      const capturedTrace = streamingTraceRef.current.length > 0 ? [...streamingTraceRef.current] : undefined;
      streamingTraceRef.current = [];
      setStreamingSources([]);
      setStreamingChartData(null);

      if (activeSessionId2) {
        const endTime = new Date().toISOString();
        try {
          await chatSessionApi.saveAssistantMessage({
            sessionId: activeSessionId2,
            chatId: String(currentChatId),
            assistantContent: fullContent,
            endDateTime: endTime,
            inputToken: usage?.inputToken,
            outputToken: usage?.outputToken,
            totalToken: usage?.totalToken,
            resultTime: ((performance.now() - startedAt) / 1000).toFixed(1),
            status: 'complete',
            messageId: msgId,
            sources,
            traceContent: capturedTrace,
          });
          setChatIdCounter(prev => prev + 1);
          if (currentChatId === 1) loadSessions(workflowId);
        } catch { /* 무시 */ }
      }
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
      const errContent = `⚠️ 오류가 발생했습니다.\n\n${errMsg}`;
      showToast('error', '응답 생성 실패');
      setStreamingContent('');
      setStreamingSources([]);
      setStreamingChartData(null);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        chatId: String(currentChatId),
        messageId: msgId,
        role: 'assistant',
        content: errContent,
      }]);
      if (activeSessionId2) {
        chatSessionApi.saveAssistantMessage({
          sessionId: activeSessionId2,
          chatId: String(currentChatId),
          assistantContent: errContent,
          endDateTime: new Date().toISOString(),
          status: 'error',
          messageId: msgId,
        }).catch(() => {});
      }
    } finally {
      setTyping(false);
    }
  };

  // ── 새 대화 ──
  const handleClear = () => {
    setMessages([]);
    setCurrentSessionId(null);
    setChatIdCounter(1);
    setPreviewHtml(null);
  };

  // ── 세션 불러오기 ──
  const handleLoadSession = async (sessionId: string) => {
    setPreviewHtml(null);
    try {
      const items = await chatSessionApi.getSession(sessionId);
      const paired: Message[] = [];
      items.forEach(i => {
        if (i.userContent) paired.push({ id: `u-${i.chatId}`, chatId: i.chatId, messageId: i.messageId, role: 'user', content: i.userContent });
        if (i.assistantContent) {
          const inputToken = i.inputToken ?? 0;
          const outputToken = i.outputToken ?? 0;
          const usage = (inputToken || outputToken) ? {
            inputToken, outputToken, totalToken: i.totalToken ?? 0,
            ...calcCostFront(i.modelId || '', inputToken, outputToken),
          } : undefined;
          const elapsedMs = i.resultTime ? parseFloat(i.resultTime) * 1000 : undefined;
          paired.push({ id: `a-${i.chatId}`, chatId: i.chatId, messageId: i.messageId, role: 'assistant', content: i.assistantContent, usage, sources: i.sources, elapsedMs, traceContent: i.traceContent?.length ? i.traceContent : undefined });
        }
      });
      setMessages(paired);
      setCurrentSessionId(sessionId);
      setChatIdCounter((items.length || 0) + 1);
    } catch { showToast('error', '대화를 불러오는데 실패했습니다.'); }
  };

  // ── HTML 미리보기 리사이즈 ──
  const handlePreviewResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = previewWidthRef.current;
    if (iframeRef.current) iframeRef.current.style.pointerEvents = 'none';
    const onMouseMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX;
      const newWidth = Math.max(280, Math.min(860, startWidth + delta));
      previewWidthRef.current = newWidth;
      if (layoutRef.current) {
        layoutRef.current.style.gridTemplateColumns = `260px minmax(0, 1fr) ${newWidth}px`;
      }
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (iframeRef.current) iframeRef.current.style.pointerEvents = '';
      setPreviewWidth(previewWidthRef.current);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // ── workflowId 없으면 챗봇 선택 화면 ──
  if (!workflowId) return <ChatSelectPage />;

  // ── 렌더 ──
  return (
    <>
      <Topbar
        title="챗봇"
        description="배포된 워크플로우를 테스트합니다"
        actions={
          <>
            <Button color="gray" appearance="outline" onClick={() => setThemeModalOpen(true)} disabled={!workflow}>
              <ThemeIcon className="cbot-ic" width={15} height={15} fill="currentColor" />테마 설정
            </Button>
            <Button color="gray" appearance="outline" onClick={() => navigate('/chat')}>
              <svg className="cbot-ic" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M19 12H5M11 18l-6-6 6-6" />
              </svg>목록
            </Button>
          </>
        }
      />

      <ChatRenderer
        layoutRef={layoutRef}
        messages={messages}
        typing={typing}
        streamingContent={streamingContent}
        streamingStatus={streamingStatus}
        streamingTrace={streamingTrace}
        streamingSources={streamingSources}
        streamingChartData={streamingChartData}
        onSend={handleSend}
        messagesEndRef={messagesEndRef}
        sessions={sessions}
        currentSessionId={currentSessionId}
        onNewChat={handleClear}
        onSelectSession={handleLoadSession}
        onDeleteSession={async (sid) => {
          await chatSessionApi.deleteSession(sid);
          setSessions(prev => prev.filter(s => s.sessionId !== sid));
          if (currentSessionId === sid) handleClear();
        }}
        onRenameSession={async (sid, title) => {
          await chatSessionApi.renameSession(sid, title);
          setSessions(prev => prev.map(s => s.sessionId === sid ? { ...s, title } : s));
        }}
        botName={botName}
        workflow={workflow}
        topK={topK}
        activeTheme={activeTheme}
        quickPrompts={quickPrompts}
        onModelChange={(model) => { selectedModelRef.current = model; }}
        onHtmlPreview={(html) => setPreviewHtml(html)}
        activePreviewHtml={previewHtml}
        rightPanel={previewHtml !== null ? (
          <aside className="cbot-preview">
            <div className="cbot-preview-resize" onMouseDown={handlePreviewResize} />
            <div className="cbot-preview-head">
              <span>HTML 미리보기</span>
              <button type="button" className="cbot-ib" title="닫기" onClick={() => setPreviewHtml(null)}>
                <svg className="cbot-ic" width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <iframe
              ref={iframeRef}
              className="cbot-preview-frame"
              srcDoc={previewHtml}
              sandbox="allow-scripts allow-same-origin"
              title="HTML 미리보기"
            />
          </aside>
        ) : undefined}
        rightPanelWidth={previewHtml !== null ? previewWidth : undefined}
      />

      {/* 테마 설정 모달 */}
      <ThemeEditorModal
        isOpen={themeModalOpen}
        onClose={() => setThemeModalOpen(false)}
        initialTheme={activeTheme}
        botName={workflow?.name}
        saving={savingTheme}
        showQuickPrompts={isAdmin || isBotOwner}
        onSave={async (theme) => {
          setSavingTheme(true);
          try {
            await chatSessionApi.saveTheme(workflowId, theme);
            setPersonalTheme(theme);
            setThemeModalOpen(false);
            showToast('success', '테마 설정이 저장되었습니다.');
          } catch { showToast('error', '테마 저장에 실패했습니다.'); }
          finally { setSavingTheme(false); }
        }}
      />
    </>
  );
}

export default ChatPage;
